import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react'
import { useToast } from '@/contexts/ToastContext'

// Google OAuth Configuration
const GOOGLE_CLIENT_ID = '631871448084-c7659erc3j3r0shmqlqcat5f8gctgc70.apps.googleusercontent.com'
const PICKER_SCOPE = 'https://www.googleapis.com/auth/photospicker.mediaitems.readonly'

interface GoogleAuthState {
  isSignedIn: boolean
  isLoading: boolean
  accessToken: string | null
  tokenExpiry: number | null  // Unix timestamp when token expires
}

interface PickerPhoto {
  id: string
  createTime: string
  mediaFile: {
    filename: string
    baseUrl: string
    mimeType: string
  }
}

interface GoogleAuthContextValue {
  isSignedIn: boolean
  isLoading: boolean
  isPickerOpen: boolean
  pendingPhotos: PickerPhoto[] | null
  signIn: () => Promise<void>
  signOut: () => void
  openPhotoPicker: () => Promise<PickerPhoto[]>
  consumePendingPhotos: () => PickerPhoto[] | null
  accessToken: string | null
}

const GoogleAuthContext = createContext<GoogleAuthContextValue | null>(null)

export function useGoogleAuth() {
  const context = useContext(GoogleAuthContext)
  if (!context) {
    throw new Error('useGoogleAuth must be used within a GoogleAuthProvider')
  }
  return context
}

interface GoogleAuthProviderProps {
  children: ReactNode
}

export function GoogleAuthProvider({ children }: GoogleAuthProviderProps) {
  const { addToast } = useToast()
  const [state, setState] = useState<GoogleAuthState>({
    isSignedIn: false,
    isLoading: true,
    accessToken: null,
    tokenExpiry: null,
  })
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [pendingPhotos, setPendingPhotos] = useState<PickerPhoto[] | null>(null)

  // Refs for GIS token client and pending promise resolution
  const tokenClientRef = useRef<TokenClient | null>(null)
  const pendingAuthRef = useRef<{
    resolve: (value: void) => void
    reject: (reason: Error) => void
  } | null>(null)
  const pendingRefreshRef = useRef<{
    resolve: (token: string) => void
    reject: (reason: Error) => void
  } | null>(null)

  // Initialize GIS token client
  useEffect(() => {
    const initializeGIS = () => {
      if (typeof google === 'undefined') {
        // GIS script not loaded yet, retry
        setTimeout(initializeGIS, 100)
        return
      }

      tokenClientRef.current = google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: PICKER_SCOPE,
        callback: (response: TokenResponse) => {
          if (response.error) {
            // Handle error
            const error = new Error(response.error_description || response.error)

            if (pendingAuthRef.current) {
              pendingAuthRef.current.reject(error)
              pendingAuthRef.current = null
            }
            if (pendingRefreshRef.current) {
              pendingRefreshRef.current.reject(error)
              pendingRefreshRef.current = null
            }

            addToast({
              type: 'error',
              title: 'Authentication failed',
              description: response.error_description || response.error,
            })
            return
          }

          // Calculate token expiry time
          const tokenExpiry = Date.now() + (response.expires_in * 1000)

          setState({
            isSignedIn: true,
            isLoading: false,
            accessToken: response.access_token,
            tokenExpiry,
          })

          // Resolve any pending promises
          if (pendingAuthRef.current) {
            addToast({
              type: 'success',
              title: 'Signed in',
              description: 'Connected to Google Photos',
            })
            pendingAuthRef.current.resolve()
            pendingAuthRef.current = null
          }
          if (pendingRefreshRef.current) {
            pendingRefreshRef.current.resolve(response.access_token)
            pendingRefreshRef.current = null
          }
        },
        error_callback: (error: { type: string; message: string }) => {
          const err = new Error(error.message || error.type)

          if (pendingAuthRef.current) {
            pendingAuthRef.current.reject(err)
            pendingAuthRef.current = null
          }
          if (pendingRefreshRef.current) {
            pendingRefreshRef.current.reject(err)
            pendingRefreshRef.current = null
          }

          // Only show toast for non-popup-closed errors
          if (error.type !== 'popup_closed') {
            addToast({
              type: 'error',
              title: 'Authentication error',
              description: error.message || error.type,
            })
          }
        },
      })

      // Mark as ready
      setState(prev => ({ ...prev, isLoading: false }))
    }

    initializeGIS()
  }, [addToast])

  const signIn = useCallback(async () => {
    if (!tokenClientRef.current) {
      addToast({
        type: 'error',
        title: 'Not ready',
        description: 'Google Sign-In is still loading. Please try again.',
      })
      return
    }

    return new Promise<void>((resolve, reject) => {
      pendingAuthRef.current = { resolve, reject }
      tokenClientRef.current!.requestAccessToken()
    })
  }, [addToast])

  const signOut = useCallback(() => {
    // Revoke the token if we have one
    if (state.accessToken) {
      google.accounts.oauth2.revoke(state.accessToken)
    }

    setState({
      isSignedIn: false,
      isLoading: false,
      accessToken: null,
      tokenExpiry: null,
    })

    addToast({
      type: 'info',
      title: 'Signed out',
      description: 'Disconnected from Google Photos',
    })
  }, [state.accessToken, addToast])

  // Ensure we have a valid token, refreshing if needed
  const ensureValidToken = useCallback(async (): Promise<string> => {
    if (!state.accessToken || !state.tokenExpiry) {
      throw new Error('Not signed in')
    }

    // Check if token expires in next 5 minutes
    const bufferMs = 5 * 60 * 1000
    if (Date.now() + bufferMs > state.tokenExpiry) {
      // Token expired or expiring soon - request new one silently
      if (!tokenClientRef.current) {
        throw new Error('Token client not initialized')
      }

      return new Promise<string>((resolve, reject) => {
        pendingRefreshRef.current = { resolve, reject }
        // Request with empty prompt for silent refresh
        tokenClientRef.current!.requestAccessToken({ prompt: '' })
      })
    }

    return state.accessToken
  }, [state.accessToken, state.tokenExpiry])

  const openPhotoPicker = useCallback(async (): Promise<PickerPhoto[]> => {
    // Ensure we have a valid token (will refresh if needed)
    const accessToken = await ensureValidToken()

    // Step 1: Create a picker session
    const sessionResponse = await fetch('https://photospicker.googleapis.com/v1/sessions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!sessionResponse.ok) {
      const errorText = await sessionResponse.text()
      throw new Error(`Failed to create session: ${sessionResponse.status} ${errorText}`)
    }

    const sessionData = await sessionResponse.json()

    // Step 2: Open the picker window
    const pickerWindow = window.open(
      sessionData.pickerUri,
      'Google Photos Picker',
      'width=800,height=600'
    )

    if (!pickerWindow) {
      throw new Error('Popup blocked. Please allow popups for this site.')
    }

    setIsPickerOpen(true)

    // Step 3: Poll for photo selection
    try {
      const selectedPhotos = await pollPickerSession(sessionData.id, accessToken, pickerWindow)
      // Store photos in state so they persist even if the calling component unmounts
      setPendingPhotos(selectedPhotos)
      return selectedPhotos
    } finally {
      setIsPickerOpen(false)
    }
  }, [ensureValidToken])

  const consumePendingPhotos = useCallback(() => {
    const photos = pendingPhotos
    setPendingPhotos(null)
    return photos
  }, [pendingPhotos])

  const value: GoogleAuthContextValue = {
    isSignedIn: state.isSignedIn,
    isLoading: state.isLoading,
    isPickerOpen,
    pendingPhotos,
    signIn,
    signOut,
    openPhotoPicker,
    consumePendingPhotos,
    accessToken: state.accessToken,
  }

  return (
    <GoogleAuthContext.Provider value={value}>
      {children}
    </GoogleAuthContext.Provider>
  )
}

// Poll the picker session until photos are selected
async function pollPickerSession(sessionId: string, accessToken: string, pickerWindow: Window): Promise<PickerPhoto[]> {
  return new Promise((resolve, reject) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(
          `https://photospicker.googleapis.com/v1/sessions/${sessionId}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          }
        )

        if (!response.ok) {
          throw new Error(`Failed to get session: ${response.status}`)
        }

        const sessionData = await response.json()

        if (sessionData.mediaItemsSet) {
          // User has selected photos - close the picker window
          clearInterval(pollInterval)
          if (pickerWindow && !pickerWindow.closed) {
            pickerWindow.close()
          }

          // Fetch the selected photos
          const photos = await fetchSelectedPhotos(sessionId, accessToken)
          resolve(photos)
        }
      } catch (error) {
        clearInterval(pollInterval)
        if (pickerWindow && !pickerWindow.closed) {
          pickerWindow.close()
        }
        reject(error)
      }
    }, 3000) // Poll every 3 seconds

    // Timeout after 30 minutes
    setTimeout(() => {
      clearInterval(pollInterval)
      if (pickerWindow && !pickerWindow.closed) {
        pickerWindow.close()
      }
      reject(new Error('Photo selection timed out'))
    }, 1800000)
  })
}

// Fetch selected photos from the picker session
async function fetchSelectedPhotos(sessionId: string, accessToken: string): Promise<PickerPhoto[]> {
  let allPhotos: PickerPhoto[] = []
  let pageToken: string | null = null

  do {
    const url = new URL('https://photospicker.googleapis.com/v1/mediaItems')
    url.searchParams.append('sessionId', sessionId)
    url.searchParams.append('pageSize', '100')
    if (pageToken) {
      url.searchParams.append('pageToken', pageToken)
    }

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch photos: ${response.status}`)
    }

    const data = await response.json()
    allPhotos = allPhotos.concat(data.mediaItems || [])
    pageToken = data.nextPageToken || null
  } while (pageToken)

  return allPhotos
}
